// src/StylistApprovalPage.js - application queue, approve/reject/suspend, gates and internal notes.
// All state changes go through the admin_set_stylist_status RPC (migration 20261011_stylist_approval.sql).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Button, TextField, MenuItem, CircularProgress, Stack,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import StylistReferences from './StylistReferences';
import BackgroundCheck from './BackgroundCheck';
import { prettyStatus, ACTION_LABELS } from './labels';

const FILTERS = ['submitted', 'under_review', 'needs_more_info', 'approved', 'rejected', 'suspended', 'all'];
// stylist_readiness returns the label with the gate, so the wording an admin
// reads here is the same wording the server puts in a refusal. Falls back to
// the machine name only if a gate is added without one.
const gateLabel = (g) => g.label || g.gate;
const actionLabel = (a) => ACTION_LABELS[a] || prettyStatus(a);
const NEEDS_REASON = ['reject', 'request_info', 'suspend'];
const GOES_LIVE = ['approve', 'publish', 'reactivate'];

export default function StylistApprovalPage() {
  const [filter, setFilter] = useState('submitted');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [gates, setGates] = useState({});
  const [dialog, setDialog] = useState(null); // { row, action }
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState(false);
  const [notes, setNotes] = useState({});
  // Published stylists who no longer meet the bar. The page defaults to
  // 'submitted' and loads a checklist only when a row is expanded, so somebody
  // already live who has since fallen short is invisible — and all four
  // What each stylist on screen is still missing, keyed by id. This used to be
  // a single banner at the top listing everyone; it told you a name and a list
  // of gaps a long scroll away from the row you could act on.
  const [missing, setMissing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('Profiles')
      .select('id, first_name, last_name, email, phone_number, user_type, stylist_status, is_published, stylist_status_reason, stylist_status_updated_at, stylist_status_updated_by, admin_notes, created_at')
      .ilike('user_type', 'stylist')
      .order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('stylist_status', filter);
    const { data, error } = await q;
    if (error) setSnack({ open: true, message: `Load failed: ${error.message}`, severity: 'error' });
    setRows(data || []);
    setNotes(Object.fromEntries((data || []).map((r) => [r.id, r.admin_notes || ''])));
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Every row on screen, so each one can say what it is waiting on without
  // being expanded. One call per stylist, run together.
  useEffect(() => {
    let cancelled = false;
    if (rows.length === 0) { setMissing({}); return undefined; }
    (async () => {
      const pairs = await Promise.all(rows.map(async (row) => {
        const { data } = await supabase.rpc('stylist_readiness', { p_profile_id: row.id });
        return [row.id, (data || []).filter((g) => !g.ok).map(gateLabel)];
      }));
      if (!cancelled) setMissing(Object.fromEntries(pairs));
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const loadGates = async (row) => {
    const { data, error } = await supabase.rpc('stylist_readiness', { p_profile_id: row.id });
    if (error) return setSnack({ open: true, message: `Checklist failed: ${error.message}`, severity: 'error' });
    setGates((g) => ({ ...g, [row.id]: data }));
  };

  const run = async () => {
    const { row, action } = dialog;
    const { error } = await supabase.rpc('admin_set_stylist_status', {
      p_profile_id: row.id, p_action: action, p_reason: reason || null, p_override: override,
    });
    if (error) {
      setSnack({ open: true, message: error.message, severity: 'error' });
      return;
    }
    setDialog(null); setReason(''); setOverride(false);
    setSnack({ open: true, message: `Done — ${actionLabel(action)}`, severity: 'success' });
    load();
  };

  const saveNote = async (row) => {
    // Through the RPC, not a direct update. The only UPDATE policy on Profiles
    // is "users can update own profile", so this table write matched zero rows
    // as an admin and PostgREST returned success — notes looked saved and were
    // gone on the next load.
    const { error } = await supabase.rpc('admin_set_profile_note', {
      p_profile_id: row.id,
      p_note: notes[row.id] || null,
    });
    setSnack(error
      ? { open: true, message: `Note failed: ${error.message}`, severity: 'error' }
      : { open: true, message: 'Note saved', severity: 'success' });
  };

  const actionsFor = (r) => {
    switch (r.stylist_status) {
      case 'approved': return r.is_published ? ['unpublish', 'suspend'] : ['publish', 'suspend'];
      case 'suspended': return ['reactivate'];
      case 'rejected': return ['under_review'];
      default: return ['under_review', 'approve', 'request_info', 'reject'];
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>Stylist approval</Typography>
        <TextField select size="small" label="Status" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 180 }}>
          {FILTERS.map((f) => <MenuItem key={f} value={f}>{prettyStatus(f)}</MenuItem>)}
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>
      {loading && <CircularProgress size={24} />}
      {!loading && rows.length === 0 && <Typography color="text.secondary">No stylists in this state.</Typography>}
      {rows.map((r) => (
        <Paper key={r.id} sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 600, flex: 1 }}>{r.first_name} {r.last_name} · {r.email || r.phone_number}</Typography>
            <Chip size="small" label={prettyStatus(r.stylist_status) || 'No status'} />
            <Chip size="small" color={r.is_published ? 'success' : 'default'} label={r.is_published ? 'Published' : 'Not published'} />
          </Stack>
          {/* A published stylist with gaps is the serious case: she is bookable
              right now. An unpublished one is simply not finished yet. */}
          {missing[r.id] && missing[r.id].length > 0 && (
            <Typography
              variant="body2"
              sx={{ mb: 1, color: r.is_published ? 'error.main' : 'text.secondary' }}
            >
              {r.is_published ? 'Live and bookable, but missing: ' : 'Waiting on: '}
              {missing[r.id].join(', ')}
            </Typography>
          )}
          {missing[r.id] && missing[r.id].length === 0 && !r.is_published && (
            <Typography variant="body2" sx={{ mb: 1, color: 'success.main' }}>
              Every gate passes — publishes itself once the background check clears.
            </Typography>
          )}
          {r.stylist_status_reason && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Reason: {r.stylist_status_reason} ({r.stylist_status_updated_by}, {new Date(r.stylist_status_updated_at).toLocaleDateString()})
            </Typography>
          )}
          {gates[r.id] ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {gates[r.id].map((g) => (
                <Chip key={g.gate} size="small" color={g.ok ? 'success' : 'error'} variant="outlined" title={g.detail}
                  label={`${g.ok ? '✓' : '✗'} ${gateLabel(g)}`} />
              ))}
            </Stack>
          ) : (
            <Button size="small" onClick={() => loadGates(r)} sx={{ mb: 1 }}>Show go-live checklist</Button>
          )}
          {/* The references this stylist gave at onboarding. Collected since
              StylistOnboardingView was written and readable by admins all
              along; nothing here had ever shown them. */}
          <StylistReferences stylistPhone={r.phone_number} />
          {/* Opt-in, so its absence is not a failing gate — it decides a badge,
              not whether she can go live. */}
          <BackgroundCheck stylistId={r.id} />
          <TextField fullWidth multiline minRows={2} size="small" label="Internal notes (admins only)"
            value={notes[r.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
            onBlur={() => (notes[r.id] || '') !== (r.admin_notes || '') && saveNote(r)} sx={{ mb: 1 }} />
          <Stack direction="row" spacing={1}>
            {actionsFor(r).map((a) => (
              <Button key={a} size="small" variant="outlined"
                color={['reject', 'suspend'].includes(a) ? 'error' : 'primary'}
                onClick={() => { setDialog({ row: r, action: a }); setReason(''); setOverride(false); }}>
                {actionLabel(a)}
              </Button>
            ))}
          </Stack>
        </Paper>
      ))}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialog && `${actionLabel(dialog.action)} — ${dialog.row.first_name} ${dialog.row.last_name}`}</DialogTitle>
        {/* A Stack rather than siblings: the caption variant renders inline, so
            next to the checkbox label it wrapped around it instead of sitting
            underneath. */}
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {dialog && NEEDS_REASON.includes(dialog.action) && (
              <TextField autoFocus fullWidth multiline minRows={3} label="Reason (required)"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
            {dialog && GOES_LIVE.includes(dialog.action) && (
              <>
                <Typography variant="body2">
                  This puts the stylist in the public list. It is refused if any go-live gate is
                  failing. A stylist who clears their background check and passes every gate is
                  published automatically, so this is only needed to publish someone sooner.
                </Typography>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start' }}
                  control={<Checkbox sx={{ pt: 0 }} checked={override} onChange={(e) => setOverride(e.target.checked)} />}
                  label="Override failing gates (recorded against your name)" />
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              The stylist is not emailed about a decision made here. Contact them from Chats or
              by email. Automatic approvals do send the stylist an email.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={run} disabled={!!dialog && NEEDS_REASON.includes(dialog.action) && !reason.trim()}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
